import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, FileText, ArrowLeft } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { getAssignmentPreview, enterAssignment } from '../lib/api';
import { DraftyMascot } from './DraftyMascot';
import { toast } from 'sonner';
import { DemoChip } from './ui/demo-chip';

const LOGO_URL = '/images/DraftFlow-billboard.png';

const AssignmentJoin = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, signInWithGoogle, profile } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loadingAssignment, setLoadingAssignment] = useState(true);
  const [assignmentError, setAssignmentError] = useState(null);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [needsCode, setNeedsCode] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [codeSubmitting, setCodeSubmitting] = useState(false);

  // Fetch the public assignment preview — works BEFORE sign-in, so the
  // student sees what they're joining (title, class, teacher)
  useEffect(() => {
    if (!assignmentId) {
      setLoadingAssignment(false);
      return;
    }

    const fetchPreview = async () => {
      try {
        const data = await getAssignmentPreview(assignmentId);
        setPreview(data);
      } catch (error) {
        console.error('Failed to fetch assignment preview:', error);
        setAssignmentError('Assignment not found. Check the link with your teacher.');
      } finally {
        setLoadingAssignment(false);
      }
    };

    fetchPreview();
  }, [assignmentId]);

  // Once authenticated with a profile: one entry call auto-enrolls the
  // student and creates their draft, then straight to the editor.
  useEffect(() => {
    if (!isAuthenticated || !profile || isSettingUp || needsCode) return;

    const setupAndRedirect = async () => {
      setIsSettingUp(true);
      try {
        const data = await enterAssignment(assignmentId);
        toast.success(`Opening: ${data.assignment?.title || 'Assignment'}`);
        navigate(`/write/${assignmentId}`, { replace: true });
      } catch (error) {
        console.error('Assignment entry error:', error);
        if (error?.response?.status === 410) {
          setAssignmentError('This assignment is closed. Ask your teacher for a new link.');
        } else if (error?.response?.status === 403 && error?.response?.data?.detail?.code) {
          // Roster-or-code gate: ask for the class code instead of bouncing
          setNeedsCode(true);
          setIsSettingUp(false);
          return;
        } else if (error?.response?.status === 404) {
          setAssignmentError('Assignment not found. Check the link with your teacher.');
        } else {
          toast.error('Something went wrong opening your assignment.');
          navigate('/dashboard', { replace: true });
        }
        setIsSettingUp(false);
      }
    };

    setupAndRedirect();
  }, [isAuthenticated, profile, needsCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitCode = async () => {
    if (!codeInput.trim() || codeSubmitting) return;
    setCodeSubmitting(true);
    setCodeError(null);
    try {
      const data = await enterAssignment(assignmentId, codeInput.trim());
      toast.success(`Welcome to ${data.assignment?.title || 'your assignment'}!`);
      navigate(`/write/${assignmentId}`, { replace: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setCodeError(
        detail?.code === 'invalid_code'
          ? "That code doesn't match this class. Double-check it with your teacher."
          : 'Something went wrong. Try again.'
      );
    } finally {
      setCodeSubmitting(false);
    }
  };

  // Loading state
  if (authLoading || (isAuthenticated && loadingAssignment) || isSettingUp) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-flow-blue border-t-transparent mx-auto mb-4" />
          <p className="text-slate-400">
            {isSettingUp ? 'Setting up your draft...' : 'Loading assignment...'}
          </p>
        </div>
      </div>
    );
  }

  // Roster-or-code gate — signed in but not on this class roster
  if (isAuthenticated && needsCode) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
          <div className="text-center mb-8">
            <DraftyMascot size="lg" mood="thinking" className="mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-white">Almost in!</h1>
            <p className="text-slate-400 mt-2">
              You're not on the roster for {preview?.class_name ? <span className="text-white">{preview.class_name}</span> : 'this class'} yet.
              Enter your class code to join.
            </p>
          </div>
          <div className="card-surface p-6">
            <input
              data-testid="class-code-input"
              type="text"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') submitCode(); }}
              placeholder="CLASS CODE"
              maxLength={6}
              className="w-full bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 text-center font-mono text-xl tracking-[0.3em] text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-flow-blue"
            />
            {codeError && <p className="text-red-400 text-sm mt-3 text-center" data-testid="code-error">{codeError}</p>}
            <button
              data-testid="submit-class-code"
              onClick={submitCode}
              disabled={codeSubmitting || codeInput.trim().length < 4}
              className="w-full btn-teal py-3 rounded-xl mt-4 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {codeSubmitting ? <Loader2 size={18} className="animate-spin" /> : null}
              Join and start writing
            </button>
            <p className="text-slate-500 text-xs text-center mt-4">
              Your teacher's class code is on their Classes page. Ask them for it.
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (assignmentError) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <DraftyMascot size="lg" mood="thinking" className="mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-white mb-2">Assignment Not Found</h1>
          <p className="text-slate-400 mb-6">{assignmentError}</p>
          <Link
            to="/"
            className="text-flow-blue-hover hover:underline text-sm"
          >
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  // Unauthenticated — show assignment preview and sign-in prompt
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 border-b border-slate-800">
        <nav className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="DraftFlow" className="h-10" />
          </Link>
          <Link
            to="/"
            data-testid="back-to-home"
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-8">
            <DraftyMascot size="lg" mood="excited" className="mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold text-white">
              Your Teacher Shared an Assignment
            </h1>
            <p className="text-slate-400 mt-2">
              Sign in with Google to start writing.
            </p>
          </div>

          <div className="card-surface p-6">
            <div className="flex items-center gap-3 mb-6 p-3 bg-slate-900/50 rounded-lg">
              <FileText className="text-teal-flow flex-shrink-0" size={24} />
              <div>
                <p className="text-white font-medium">{preview?.title || 'Assignment'}</p>
                <p className="text-slate-400 text-sm">
                  {[preview?.class_name, preview?.teacher_name].filter(Boolean).join(' · ') ||
                    `${assignmentId?.slice(0, 8)}...`}
                </p>
              </div>
            </div>

            <DemoChip label="Sign in to join assignments">
              <button
                data-testid="signin-for-assignment-btn"
                onClick={() => {
                  // Come back to this assignment after the OAuth round-trip
                  sessionStorage.setItem(
                    'draftflow_post_login_redirect',
                    `/join/assignment/${assignmentId}`
                  );
                  signInWithGoogle('student');
                }}
                className="w-full flex items-center justify-center gap-3 bg-white text-slate-900 px-6 py-3 rounded-lg font-medium hover:bg-slate-100 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in to Start Writing
              </button>
            </DemoChip>

            <p className="mt-4 text-xs text-slate-500 text-center">
              By signing in, you agree to our{' '}
              <Link to="/terms" className="text-slate-400 hover:text-white underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-slate-400 hover:text-white underline">Privacy Policy</Link>.
            </p>
          </div>

          <p className="text-center mt-6 text-sm text-slate-500">
            Have a class code instead?{' '}
            <Link to="/join" className="text-flow-blue-hover hover:underline">
              Join a class
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AssignmentJoin;
